import React, { FC, Fragment, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { CaseListSourceStatuses, ScannerCaseFilter, ScannerCaseSortOrder } from 'models/scanner-case';
import ScannerSourceStatus from 'components/scanner-cases/labs/scanner-source-status/scanner-source-status.component';
import ScannerCaseTable from 'components/scanner-cases/labs/scanner-case-table/scanner-case-table.component';
import ScannerCasesPageActions from 'components/scanner-cases/labs/scanner-cases-page-actions.component';
import ScannerCaseAuthContainer from './auth-container/auth-container.component';
import { isEqual } from 'lodash';

const SCANNER_CASE_SORT_ORDER_KEY = 'scannerCaseSortOrder';

const getStoredSortOrder = (): ScannerCaseSortOrder => {
    const storedSortOrder = localStorage.getItem(SCANNER_CASE_SORT_ORDER_KEY);
    return storedSortOrder === ScannerCaseSortOrder.OLDEST_FIRST
        ? ScannerCaseSortOrder.OLDEST_FIRST
        : ScannerCaseSortOrder.NEWEST_FIRST;
};

const ScannerCases: FC = () => {
    const { t: translate } = useTranslation(['scan-cases']);
    const [scannerCaseFilter, setScannerCaseFilter] = useState<ScannerCaseFilter>(
        JSON.parse(localStorage.getItem('scannerCaseFilter') || '{"orderStatus": "NEW"}'),
    );
    const [sortOrder, setSortOrder] = useState<ScannerCaseSortOrder>(getStoredSortOrder);
    const [sourceStatus, setSourceStatus] = useState<CaseListSourceStatuses>();

    const handleSortOrderChange = (newSortOrder: ScannerCaseSortOrder) => {
        localStorage.setItem(SCANNER_CASE_SORT_ORDER_KEY, newSortOrder);
        setSortOrder(newSortOrder);
    };

    const handleFilterChange = useCallback(
        (filters: ScannerCaseFilter) => {
            if (!isEqual(filters, scannerCaseFilter)) {
                localStorage.setItem('scannerCaseFilter', JSON.stringify(filters));
                setScannerCaseFilter(filters);
            }
        },
        [scannerCaseFilter],
    );

    const handleSourceStatusChange = (status: CaseListSourceStatuses) => {
        setSourceStatus(status);
    };

    return (
        <Fragment>
            <Helmet>
                <title>{translate('scan-cases:SCAN_CASES')} | Denthub</title>
            </Helmet>
            <ScannerCaseAuthContainer />
            <Box mb={2} display="flex" alignItems="center">
                <Box flexGrow={1} mr={2}>
                    <ScannerCasesPageActions initFilters={scannerCaseFilter} onFilterChange={handleFilterChange} />
                </Box>
                <Box>
                    <ScannerSourceStatus status={sourceStatus} />
                </Box>
            </Box>
            <ScannerCaseTable
                scannerCaseFilter={scannerCaseFilter}
                sortOrder={sortOrder}
                onSortOrderChange={handleSortOrderChange}
                onScanSourceStatusChange={handleSourceStatusChange}
            />
        </Fragment>
    );
};

export default ScannerCases;
